import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, Link2 } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { CtaWhatsApp } from "../components/Cta";
import { Reveal } from "../components/motion";
import CardMockup, { CardPair } from "../components/CardMockup";
import { METIERS } from "../data/metiers";
import { METIER_ICONS } from "./Solutions";
import { SITE_URL, PRICE_TEASER, RESPONSE_TIME, DELIVERY_DELAY } from "../config/site";
import NotFound from "./NotFound";

export default function SolutionMetier() {
  const { slug } = useParams();
  const metier = METIERS.find((m) => m.slug === slug);
  if (!metier) return <NotFound />;

  const Icon = METIER_ICONS[metier.icon];
  const others = METIERS.filter((m) => m.slug !== slug).slice(0, 4);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Carte digitale NFC personnalisée pour ${metier.name.toLowerCase()}`,
    provider: { "@type": "Organization", name: "Noveocard", url: SITE_URL },
    description: metier.metaDesc,
    areaServed: "FR",
    offers: {
      "@type": "AggregateOffer",
      lowPrice: "40",
      highPrice: "80",
      priceCurrency: "EUR",
    },
  };

  return (
    <>
      <Seo
        title={metier.metaTitle}
        description={metier.metaDesc}
        path={`/solutions/${metier.slug}`}
        jsonld={[serviceSchema]}
      />

      <PageHero
        dark
        eyebrow={`Solution — ${metier.name}`}
        titleLines={metier.h1}
        description={metier.intro}
      >
        <CtaWhatsApp
          message={`Bonjour Noveocard, je suis ${metier.name.toLowerCase().replace(/s$/, "")} et je souhaite commander ma carte digitale personnalisée.`}
          showResponseTime
          testid="metier-hero-cta"
        />
      </PageHero>

      {/* 01 — Le constat */}
      <section className="bg-white py-16 sm:py-24">
        <div className="container-nv grid items-start gap-10 lg:grid-cols-[auto_1fr]">
          <Reveal>
            <span className="font-display text-7xl font-bold text-brume-300 sm:text-8xl" aria-hidden="true">01</span>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow">Le constat</p>
            <h2 className="mt-3 max-w-xl font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-4xl">
              Le moment le plus précieux est aussi le plus fragile.
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-steel sm:text-lg">{metier.problem}</p>
          </Reveal>
        </div>
      </section>

      {/* 02 — Cas d'usage */}
      <section className="grain relative overflow-hidden bg-nuit-900 py-16 text-white sm:py-24">
        <div className="container-nv">
          <Reveal>
            <p className="eyebrow text-electric-300">02 — Vos cas d'usage</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Ce que la carte change pour vous, concrètement.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {metier.useCases.map((u, i) => (
              <Reveal key={u.t} delay={i * 0.08}>
                <article className="h-full rounded-3xl border border-white/10 bg-white/5 p-7 backdrop-blur-sm transition-colors duration-500 hover:border-electric/50 hover:bg-white/10">
                  <span className="font-display text-sm font-semibold text-electric-300">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-2 font-display text-xl font-bold">{u.t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/70">{u.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 03 — Exemple concret + bénéfices */}
      <section className="bg-brume py-16 sm:py-24">
        <div className="container-nv grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow">03 — Exemple concret</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-4xl">
              Une journée avec votre carte.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">{metier.example}</p>
            <ul className="mt-7 space-y-3">
              {metier.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm font-medium text-encre sm:text-base">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-electric/10">
                    <Check className="h-3 w-3 text-electric" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative mx-auto max-w-md">
              <div className="absolute -inset-10 rounded-full bg-electric/15 blur-3xl" aria-hidden="true" />
              <div className="animate-float-slow">
                <CardPair name={metier.name.replace(/s$/, "")} bg="linear-gradient(135deg,#071A2D 0%,#14406E 100%)" />
              </div>
              <p className="mt-6 text-center text-xs font-medium uppercase tracking-[0.2em] text-steel">
                Exemple de démonstration — votre carte sera conçue à votre image
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {metier.suggestedLinks.map((l) => (
                  <span
                    key={l}
                    className="inline-flex items-center gap-1.5 rounded-full border border-nuit-900/10 bg-white px-3.5 py-1.5 text-xs font-medium text-nuit-900"
                  >
                    <Link2 className="h-3 w-3 text-electric" /> {l}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bandeau prix + CTA */}
      <section className="grain relative overflow-hidden bg-nuit-950 py-16 text-center text-white sm:py-20">
        <div className="container-nv">
          <Reveal>
            <p className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{PRICE_TEASER}</p>
            <p className="mx-auto mt-3 max-w-xl text-white/65">
              {DELIVERY_DELAY} {RESPONSE_TIME} sur WhatsApp.
            </p>
            <CtaWhatsApp
              className="mt-8"
              size="lg"
              message={`Bonjour Noveocard, je souhaite commander une carte digitale pour mon activité (${metier.name.toLowerCase()}).`}
              testid="metier-bottom-cta"
            />
          </Reveal>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="bg-white py-14">
        <div className="container-nv">
          <p className="eyebrow">Autres secteurs</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((m) => {
              const OtherIcon = METIER_ICONS[m.icon];
              return (
                <Link
                  key={m.slug}
                  to={`/solutions/${m.slug}`}
                  className="group flex items-center justify-between rounded-2xl border border-nuit-900/10 px-5 py-4 transition-colors duration-300 hover:border-electric hover:bg-brume"
                >
                  <span className="flex items-center gap-3 font-display text-sm font-semibold text-nuit-900">
                    <OtherIcon className="h-4 w-4 text-electric" /> {m.name}
                  </span>
                  <ArrowRight className="h-4 w-4 text-steel transition-transform duration-300 group-hover:translate-x-1 group-hover:text-electric" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
